// ============================================================
// VCS.ChatVoice — Sprachnachrichten + Sprachbilder (Voicephoto)
// - Aufnahme (MediaRecorder), Upload, Abspielen
// - Voicephoto: Foto + Audio kombiniert senden
// - Karaoke-Effekt: Wort-fuer-Wort-Hervorhebung bei Wiedergabe
// - Unterstuetzt Original- und uebersetzte Woerter
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatVoice = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {};
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async startRecording() {
        const _t = (key, fb) => { const v = TRANSLATIONS[VCS.state.currentLang]?.[key] || TRANSLATIONS.de[key]; return v || fb; };

        // ── Overlay-Dialog (nicht fullscreen) ──
        document.querySelector('.chat-voice-rec-overlay')?.remove();

        const overlay = document.createElement('div');
        overlay.className = 'chat-voice-rec-overlay';
        const panel = document.createElement('div');
        panel.className = 'chat-voice-rec-dialog';
        panel.innerHTML = `
            <button type="button" class="chat-voice-rec-close-btn" title="${escapeHtml(_t('cancel', 'Abbrechen'))}">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <div class="chat-voice-rec-dialog-ready">
                <button type="button" class="chat-voice-rec-round-btn" title="${escapeHtml(_t('chatVoiceStartBtn', 'Jetzt sprechen'))}">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4z"/>
                        <path d="M19 10a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V20a1 1 0 0 0 2 0v-3.07A7 7 0 0 0 19 10z"/>
                    </svg>
                </button>
                <span class="chat-voice-rec-dialog-label">${escapeHtml(_t('chatVoiceReadyHint', 'Tippe auf den Button um die Aufnahme zu starten'))}</span>
            </div>`;
        overlay.appendChild(panel);
        document.body.appendChild(overlay);

        const removeAll = () => overlay.remove();

        // Backdrop-Klick schließt
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) removeAll();
        });

        // X-Button
        panel.querySelector('.chat-voice-rec-close-btn').addEventListener('click', removeAll);

        // ── Runder Button startet die Aufnahme ──
        panel.querySelector('.chat-voice-rec-round-btn').addEventListener('click', async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
                });

                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                const actx = new AudioCtx({sampleRate: 48000});
                const source = actx.createMediaStreamSource(stream);
                const dest = actx.createMediaStreamDestination();
                source.connect(dest);
                const recordStream = dest.stream;

                const analyser = actx.createAnalyser();
                analyser.fftSize = 256;
                source.connect(analyser);
                const bufLen = analyser.frequencyBinCount;
                const dataArr = new Uint8Array(bufLen);

                const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
                    : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '';

                // Dialog auf Recording-Ansicht umschalten
                panel.innerHTML = `
                    <div class="chat-voice-rec-dialog-recording">
                        <div class="chat-voice-rec-indicator">
                            <span class="chat-recording-dot"></span>
                            <span class="chat-voice-rec-time">0:00</span>
                        </div>
                        <canvas class="chat-voice-rec-canvas" width="232" height="48"></canvas>
                        <div class="chat-voice-rec-btn-row">
                            <button type="button" class="chat-voice-rec-round-btn chat-voice-rec-round-btn--cancel" title="${escapeHtml(_t('cancel', 'Abbrechen'))}">
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                            <button type="button" class="chat-voice-rec-round-btn chat-voice-rec-round-btn--active" title="${escapeHtml(_t('tooltipSend', 'Senden'))}">
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            </button>
                        </div>
                    </div>`;

                const canvas = panel.querySelector('.chat-voice-rec-canvas');
                const ctx = canvas.getContext('2d');
                const timeEl = panel.querySelector('.chat-voice-rec-time');
                let animId = null;
                const recStart = Date.now();

                const drawVisualizer = () => {
                    animId = requestAnimationFrame(drawVisualizer);
                    analyser.getByteFrequencyData(dataArr);

                    const w = canvas.width;
                    const h = canvas.height;
                    ctx.clearRect(0, 0, w, h);

                    const barCount = 32;
                    const gap = 2;
                    const barW = (w - (barCount - 1) * gap) / barCount;
                    const centerY = h / 2;

                    for (let i = 0; i < barCount; i++) {
                        const idx = Math.floor((i / barCount) * bufLen);
                        const val = dataArr[idx] / 255;
                        const barH = Math.max(3, val * (h * 0.9));

                        ctx.fillStyle = `rgba(239, 68, 68, ${0.4 + val * 0.6})`;
                        ctx.beginPath();
                        const r = Math.min(barW / 2, 2);
                        const x = i * (barW + gap);
                        const y = centerY - barH / 2;
                        ctx.roundRect(x, y, barW, barH, r);
                        ctx.fill();
                    }

                    const elapsed = Date.now() - recStart;
                    const m = Math.floor(elapsed / 60000);
                    const s = Math.floor((elapsed % 60000) / 1000);
                    timeEl.textContent = m + ':' + String(s).padStart(2, '0');
                };
                drawVisualizer();

                // MediaRecorder
                VCS.state.chatMediaRecorder = new MediaRecorder(recordStream, {
                    ...(mimeType ? {mimeType} : {}),
                    audioBitsPerSecond: 128000
                });
                VCS.state.chatMediaRecorder._cleanup = () => {
                    if (animId) cancelAnimationFrame(animId);
                    animId = null;
                    stream.getTracks().forEach(t => t.stop());
                    actx.close().catch(() => {});
                    overlay.remove();
                };
                const chunks = [];

                VCS.state.chatMediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) chunks.push(e.data);
                };

                VCS.state.chatMediaRecorder.onstop = async () => {
                    VCS.state.chatMediaRecorder?._cleanup?.();
                    const blob = new Blob(chunks, {type: mimeType});
                    const durationMs = Date.now() - VCS.state.chatRecordStart;
                    VCS.chatRecordingUI?.resetRecordingUi?.();
                    if (durationMs < 500 || blob.size === 0) return;
                    await this.uploadVoice(blob, durationMs, mimeType);
                };

                panel.querySelector('.chat-voice-rec-round-btn--cancel').addEventListener('click', () => {
                    this.cancelRecording();
                });
                panel.querySelector('.chat-voice-rec-round-btn--active').addEventListener('click', () => {
                    this.stopRecording();
                });

                VCS.state.chatMediaRecorder.start();
                VCS.state.chatRecordStart = recStart;
                VCS.chatRecordingUI?.startRecordingUi?.();
            } catch (e) {
                console.error('[chat] startVoiceRecording error:', e);
                overlay.remove();
                VCS.ui?.showToast?.(t('micCheckFailed'), 'error');
            }
        });
    }

    stopRecording() {
        if (VCS.state.chatMediaRecorder && VCS.state.chatMediaRecorder.state !== 'inactive') {
            VCS.state.chatMediaRecorder.stop();
        }
    }

    cancelRecording() {
        if (VCS.state.chatMediaRecorder && VCS.state.chatMediaRecorder.state !== 'inactive') {
            VCS.state.chatMediaRecorder.onstop = () => {
                VCS.state.chatMediaRecorder?._cleanup?.();
                VCS.chatRecordingUI?.resetRecordingUi?.();
            };
            VCS.state.chatMediaRecorder.stop();
        } else {
            VCS.chatRecordingUI?.resetRecordingUi?.();
        }
    }

    async uploadVoice(blob, durationMs, mimeType) {
        if (!VCS.state.chatActiveChatId) {
            return;
        }

        const ext = mimeType.includes('mp4') ? '.m4a' : '.webm';

        let uploadBlob = blob;
        let voiceEncrypted = false;
        let voiceIv = null;
        let voiceKeyVersion = 1;
        let voiceRecipientKeyVersion = 1;

        if (VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId] && typeof E2E !== 'undefined') {
            const sharedKey = await VCS.e2e?.getOrDeriveKey?.(VCS.state.chatActiveChatId);
            if (sharedKey) {
                const enc = await E2E.encryptBlob(sharedKey, blob);
                uploadBlob = enc.encryptedBlob;
                voiceIv = enc.iv;
                voiceEncrypted = true;
                if (sharedKey._versionInfo) {
                    voiceKeyVersion = sharedKey._versionInfo.senderVersion;
                    voiceRecipientKeyVersion = sharedKey._versionInfo.recipientVersion;
                }
            }
        }

        const formData = new FormData();
        formData.append('audio', uploadBlob, 'voice' + ext);
        formData.append('chatId', VCS.state.chatActiveChatId);
        formData.append('senderLang', VCS.state.currentLang);
        formData.append('durationMs', durationMs.toString());
        formData.append('translate', voiceEncrypted ? 'false' : (!VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId] ? 'true' : 'false'));
        if (voiceEncrypted) {
            formData.append('encrypted', 'true');
            formData.append('iv', voiceIv);
            formData.append('keyVersion', voiceKeyVersion.toString());
            formData.append('recipientKeyVersion', voiceRecipientKeyVersion.toString());
        }

        const tempMsg = {
            id                : 'temp_' + Date.now(),
            chatId            : VCS.state.chatActiveChatId,
            senderPhone       : VCS.state.chatMyPhone,
            originalText      : '',
            translatedText    : null,
            senderLang        : VCS.state.currentLang,
            timestamp         : Date.now(),
            status            : 'sent',
            audioUrl          : 'pending',
            audioDurationMs   : durationMs,
            translatedAudioUrl: null,
            _sending          : true
        };

        if (!VCS.state.chatMessages[VCS.state.chatActiveChatId]) {
            VCS.state.chatMessages[VCS.state.chatActiveChatId] = [];
        }
        VCS.state.chatMessages[VCS.state.chatActiveChatId].push(tempMsg);
        VCS.chatMessages?.renderChatMessages?.();
        VCS.chatMessages?.scrollToBottom?.();

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/messages/voice', {
                method : 'POST',
                headers: VCS.chat?.authHeaders(),
                body   : formData
            });

            if (res.ok) {
                const sent = await res.json();
                const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId];
                if (msgs) {
                    const tempIdx = msgs.findIndex(m => m.id === tempMsg.id);
                    if (tempIdx !== -1) {
                        msgs.splice(tempIdx, 1);
                    }
                    if (!msgs.some(m => m.id === sent.id)) {
                        VCS.chatMessages?.applyPendingStatus?.(sent);
                        msgs.push(sent);
                    }
                    VCS.chatMessages?.renderChatMessages?.();
                    VCS.chatMessages?.scrollToBottom?.();
                }
                VCS.chatMessages?.updateChatPreview?.(VCS.state.chatActiveChatId, t('chatVoiceMessage'), Date.now());
            }
        } catch (e) {
            console.error('[chat] uploadVoice error:', e);
        }
    }

    getAudioDuration(blob) {
        return this._getAudioDuration(blob);
    }

    async playAudio(messageId, type) {
        const isSameTrack = VCS.state.chatCurrentlyPlaying &&
            VCS.state.chatCurrentlyPlaying.messageId === messageId &&
            VCS.state.chatCurrentlyPlaying.type === type;

        if (VCS.state.chatCurrentlyPlaying && !isSameTrack) {
            const prev = VCS.state.chatAudioPlayers[VCS.state.chatCurrentlyPlaying.messageId];
            if (prev && prev[VCS.state.chatCurrentlyPlaying.type]) {
                prev[VCS.state.chatCurrentlyPlaying.type].pause();
                this.updateVoiceUI(VCS.state.chatCurrentlyPlaying.messageId, VCS.state.chatCurrentlyPlaying.type, false);
            }
            VCS.state.chatCurrentlyPlaying = null;
        }

        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        if (!msg) {
            return;
        }

        let url;
        if (type === 'media') {
            url = msg.mediaUrl;
        } else if (type === 'translated') {
            url = msg.translatedAudioUrl;
        } else {
            url = msg.audioUrl;
        }
        if (!url || url === 'pending') {
            return;
        }

        if (!VCS.state.chatAudioPlayers[messageId]) {
            VCS.state.chatAudioPlayers[messageId] = {};
        }
        let audio = VCS.state.chatAudioPlayers[messageId][type];

        if (!audio) {
            // Translation-Audio kann eigenen IV haben (translatedAudioIv) — dann Decrypt-Pfad nutzen
            const needsDecrypt = (type === 'translated' && msg.translatedAudioIv) || (type !== 'translated' && msg.encrypted && msg.iv);
            if (needsDecrypt && typeof E2E !== 'undefined') {
                VCS.e2e?.decryptAndPlayAudio?.(messageId, type, url, msg).catch(e => console.error('[E2E] audio decrypt error:', e));
                return;
            }
            // Group-Voice auf Apple-Geraeten: mediaCache (IndexedDB) umgehen — direkt vom Server streamen.
            // Server-Cache-Header (no-cache + Last-Modified + 304) übernehmen HTTP-Caching.
            // Hintergrund: Restbestände kaputter Blobs aus Pre-Patch-Phase verursachten errCode=4 in Safari.
            const isGroupVoice = !!(msg.isGroup || (VCS.state.chatActiveIsGroup && VCS.state.chatActiveChatId === msg.chatId));
            const bypassCache = isGroupVoice && VCS.mediaCache?.isAppleDevice?.() !== false;
            let cachedUrl;
            if (bypassCache) {
                cachedUrl = url;
            } else {
                cachedUrl = await VCS.mediaCache?.get?.(url) || url;
            }
            audio = new Audio(cachedUrl);
            VCS.state.chatAudioPlayers[messageId][type] = audio;

            const storedMs = msg.audioDurationMs || msg.durationMs || 0;
            const getAudioDur = () => isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (storedMs / 1000 || 0);

            audio.ontimeupdate = () => {
                const dur = getAudioDur();
                const progress = dur ? (audio.currentTime / dur) * 100 : 0;
                this.updateWaveformProgress(messageId, type, Math.min(progress, 100));
                const durEl = document.querySelector(`[data-voice-dur="${messageId}-${type}"]`);
                if (durEl) {
                    const remaining = (dur - audio.currentTime) * 1000;
                    durEl.textContent = VCS.chat?.formatDuration?.(remaining > 0 ? remaining : 0);
                }
            };

            audio.onended = () => {
                VCS.state.chatCurrentlyPlaying = null;
                this.updateVoiceUI(messageId, type, false);
                this.updateWaveformProgress(messageId, type, 0);
            };
        }

        if (isSameTrack && !audio.paused) {
            audio.pause();
            VCS.state.chatCurrentlyPlaying = null;
            this.updateVoiceUI(messageId, type, false);
        } else {
            if (audio.ended) audio.currentTime = 0;
            audio.play().catch(() => {});
            VCS.state.chatCurrentlyPlaying = {messageId, type};
            this.updateVoiceUI(messageId, type, true);
        }
    }

    // ── Musik-Player (MP3-Dateien, mediaType 'music') ───────
    // Gleiche Abspiel-Wege wie Sprachnachrichten (playAudio / e2e.decryptAndPlayAudio,
    // type 'media'); die Anzeige haengt sich an updateVoiceUI / updateWaveformProgress.

    static MUSIC_BARS        = 60;
    static MUSIC_SPEEDS      = [1, 1.25, 1.5, 2];
    static MUSIC_PEAKS_STORE = 'vcs_music_peaks_v1';
    static MUSIC_PEAKS_MAX   = 30;
    static MUSIC_DECODE_MAX  = 40 * 1024 * 1024;

    musicPlayerHtml(msg, pendingAttr) {
        const id      = escapeHtml(msg.id);
        const audio   = VCS.state.chatAudioPlayers[msg.id]?.media;
        const cur     = VCS.state.chatCurrentlyPlaying;
        const playing = !!(cur && cur.messageId === msg.id && cur.type === 'media' && audio && !audio.paused);
        const dur     = audio && isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (msg._musicDurationS || 0);
        const pos     = audio ? audio.currentTime : 0;
        const played  = dur ? Math.round((pos / dur) * VCS.ChatVoice.MUSIC_BARS) : 0;
        const heights = this._musicPeaksGet(msg.id) || this._musicSeedHeights(msg.id);
        const bars    = heights.map((h, i) =>
            `<span class="chat-music-bar${i < played ? ' is-played' : ''}" style="height:${(h * 100).toFixed(1)}%"></span>`).join('');
        const speed   = this._musicSpeed?.[msg.id] || 1;
        const icon    = playing
            ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"></rect><rect x="14" y="5" width="4" height="14" rx="1"></rect></svg>'
            : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><polygon points="7,4 20,12 7,20"></polygon></svg>';
        return `<div class="chat-music-player" data-music="${id}" data-state="${playing ? 'playing' : 'paused'}"${pendingAttr}>
                <button class="chat-music-play" data-action="play-audio" data-id="${id}" data-variant="media" type="button" data-voice-btn="${id}-media">${icon}</button>
                <div class="chat-music-body">
                    <div class="chat-music-wave" data-music-wave="${id}">${bars}</div>
                    <div class="chat-music-meta">
                        <span class="chat-music-time-cur">${this._musicFmt(pos)}</span>
                        <span class="chat-music-time-sep">/</span>
                        <span class="chat-music-time-dur">${dur ? this._musicFmt(dur) : '--:--'}</span>
                        <span class="chat-music-spacer"></span>
                        <button class="chat-music-speed${speed !== 1 ? ' is-active' : ''}" data-action="music-speed" data-id="${id}" type="button">${this._musicSpeedLabel(speed)}</button>
                    </div>
                </div>
            </div>`;
    }

    // Spulen per Tippen/Ziehen auf der Wellenform (gebunden in chat-messages.js).
    bindMusicWave(wave) {
        const messageId = wave.dataset.musicWave;
        if (!messageId) {
            return;
        }
        let scrubbing = false;
        const ratioOf = (e) => {
            const r = wave.getBoundingClientRect();
            return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        };
        const seek = (e) => {
            const audio = VCS.state.chatAudioPlayers[messageId]?.media;
            if (!audio || !isFinite(audio.duration) || audio.duration <= 0) {
                return;
            }
            audio.currentTime = ratioOf(e) * audio.duration;
            this._updateMusicUI(messageId);
        };
        wave.addEventListener('pointerdown', (e) => {
            scrubbing = true;
            try { wave.setPointerCapture(e.pointerId); } catch (_) {}
            seek(e);
        });
        wave.addEventListener('pointermove', (e) => { if (scrubbing) seek(e); });
        const end = (e) => {
            scrubbing = false;
            try { wave.releasePointerCapture(e.pointerId); } catch (_) {}
        };
        wave.addEventListener('pointerup', end);
        wave.addEventListener('pointercancel', end);
    }

    cycleMusicSpeed(messageId) {
        this._musicSpeed = this._musicSpeed || {};
        const speeds = VCS.ChatVoice.MUSIC_SPEEDS;
        const next = speeds[(speeds.indexOf(this._musicSpeed[messageId] || 1) + 1) % speeds.length];
        this._musicSpeed[messageId] = next;
        const audio = VCS.state.chatAudioPlayers[messageId]?.media;
        if (audio) {
            audio.playbackRate = next;
        }
        const btn = document.querySelector(`[data-music="${messageId}"] .chat-music-speed`);
        if (btn) {
            btn.textContent = this._musicSpeedLabel(next);
            btn.classList.toggle('is-active', next !== 1);
        }
    }

    _updateMusicUI(messageId) {
        const root = document.querySelector(`[data-music="${messageId}"]`);
        const audio = VCS.state.chatAudioPlayers[messageId]?.media;
        if (!root || !audio) {
            return;
        }
        const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
        if (dur) {
            const msg = (VCS.state.chatMessages[VCS.state.chatActiveChatId] || []).find(m => m.id === messageId);
            if (msg) {
                msg._musicDurationS = dur;
            }
        }
        const pos = audio.ended ? 0 : audio.currentTime;
        const bars = root.querySelectorAll('.chat-music-bar');
        const played = dur ? Math.round((pos / dur) * bars.length) : 0;
        bars.forEach((b, i) => b.classList.toggle('is-played', i < played));
        const curEl = root.querySelector('.chat-music-time-cur');
        if (curEl) {
            curEl.textContent = this._musicFmt(pos);
        }
        const durEl = root.querySelector('.chat-music-time-dur');
        if (durEl && dur) {
            durEl.textContent = this._musicFmt(dur);
        }
    }

    _musicFmt(s) {
        if (!isFinite(s) || s < 0) {
            return '--:--';
        }
        s = Math.floor(s);
        const m = Math.floor(s / 60);
        const sec = String(s % 60).padStart(2, '0');
        if (m >= 60) {
            return Math.floor(m / 60) + ':' + String(m % 60).padStart(2, '0') + ':' + sec;
        }
        return m + ':' + sec;
    }

    _musicSpeedLabel(rate) {
        return (rate === 1 ? '1.0' : rate === 2 ? '2.0' : String(rate)) + '×';
    }

    // Platzhalter-Hoehen bis zur echten Wellenform -- aus der ID, damit nichts springt.
    _musicSeedHeights(seed) {
        let hash = 0;
        for (let i = 0; i < seed.length; i++) {
            hash = ((hash << 5) - hash) + seed.charCodeAt(i);
            hash |= 0;
        }
        const n = VCS.ChatVoice.MUSIC_BARS;
        const out = [];
        for (let i = 0; i < n; i++) {
            hash = (hash * 1103515245 + 12345) & 0x7fffffff;
            const center = 1 - Math.abs((i / (n - 1)) - 0.5) * 0.9;
            out.push(Math.max(0.18, Math.min(1, center * (0.45 + (hash % 1000) / 1000 * 0.55))));
        }
        return out;
    }

    // Peaks-Cache in localStorage (UI-Merker, LRU).
    _musicPeaksRead() {
        try {
            const c = JSON.parse(localStorage.getItem(VCS.ChatVoice.MUSIC_PEAKS_STORE) || 'null');
            if (c && Array.isArray(c.order) && c.data) {
                return c;
            }
        } catch (_) {}
        return { order: [], data: {} };
    }

    _musicPeaksGet(id) {
        const p = this._musicPeaksRead().data[id];
        return Array.isArray(p) && p.length === VCS.ChatVoice.MUSIC_BARS ? p : null;
    }

    _musicPeaksPut(id, peaks) {
        const c = this._musicPeaksRead();
        c.order = c.order.filter(k => k !== id);
        c.order.push(id);
        c.data[id] = peaks;
        while (c.order.length > VCS.ChatVoice.MUSIC_PEAKS_MAX) {
            delete c.data[c.order.shift()];
        }
        try { localStorage.setItem(VCS.ChatVoice.MUSIC_PEAKS_STORE, JSON.stringify(c)); } catch (_) {}
    }

    // Echte Wellenform: beim ersten Abspielen die (schon geladene bzw. entschluesselte)
    // Datei einmal dekodieren, RMS je Balken, auf den Bereich min..max gestreckt.
    async _ensureMusicPeaks(messageId, audio) {
        this._musicPeaksTried = this._musicPeaksTried || {};
        if (this._musicPeaksTried[messageId] || this._musicPeaksGet(messageId)) {
            return;
        }
        this._musicPeaksTried[messageId] = true;
        const src = audio?.currentSrc || audio?.src;
        const C = window.AudioContext || window.webkitAudioContext;
        if (!src || !C) {
            return;
        }
        try {
            const resp = await fetch(src);
            if (!resp.ok) {
                return;
            }
            const ab = await resp.arrayBuffer();
            if (ab.byteLength > VCS.ChatVoice.MUSIC_DECODE_MAX) {
                console.log('[music] peaks skip, file too large', messageId, ab.byteLength);
                return;
            }
            this._musicCtx = this._musicCtx || new C();
            const buf = await new Promise((resolve, reject) => {
                const p = this._musicCtx.decodeAudioData(ab, resolve, reject);
                if (p && p.then) {
                    p.then(resolve, reject);
                }
            });
            const n = VCS.ChatVoice.MUSIC_BARS;
            const block = Math.max(1, Math.floor(buf.length / n));
            const stride = 4;
            const rms = new Array(n).fill(0);
            for (let c = 0; c < buf.numberOfChannels; c++) {
                const data = buf.getChannelData(c);
                for (let i = 0; i < n; i++) {
                    let sum = 0;
                    let cnt = 0;
                    const start = i * block;
                    for (let j = 0; j < block; j += stride) {
                        const v = data[start + j] || 0;
                        sum += v * v;
                        cnt++;
                    }
                    rms[i] += cnt ? Math.sqrt(sum / cnt) : 0;
                }
            }
            const min = Math.min(...rms);
            const max = Math.max(...rms);
            const span = max - min || 1;
            const peaks = rms.map(v => Math.round((0.15 + 0.85 * ((v - min) / span)) * 1000) / 1000);
            this._musicPeaksPut(messageId, peaks);
            const root = document.querySelector(`[data-music="${messageId}"]`);
            root?.querySelectorAll('.chat-music-bar').forEach((b, i) => {
                b.style.height = (peaks[i] * 100).toFixed(1) + '%';
            });
            console.log('[music] peaks ready', messageId);
        } catch (e) {
            console.warn('[music] peaks decode failed', messageId, e?.message || e);
        }
    }

    updateVoiceUI(messageId, type, playing) {
        if (type === 'media') {
            const root = document.querySelector(`[data-music="${messageId}"]`);
            root?.setAttribute('data-state', playing ? 'playing' : 'paused');
            const audio = VCS.state.chatAudioPlayers[messageId]?.media;
            if (audio) {
                // Geschwindigkeit bleibt beim Neu-Anlegen des Audio-Objekts erhalten
                audio.playbackRate = this._musicSpeed?.[messageId] || 1;
                if (!audio._musicBound) {
                    audio._musicBound = true;
                    audio.addEventListener('loadedmetadata', () => this._updateMusicUI(messageId));
                }
                if (playing) {
                    this._ensureMusicPeaks(messageId, audio);
                }
            }
        }
        const btn = document.querySelector(`[data-voice-btn="${messageId}-${type}"]`);
        if (!btn) {
            return;
        }
        if (type === 'media') {
            btn.innerHTML = playing
                ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"></rect><rect x="14" y="5" width="4" height="14" rx="1"></rect></svg>'
                : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><polygon points="7,4 20,12 7,20"></polygon></svg>';
            return;
        }
        if (playing) {
            btn.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="5" width="4" height="14"></rect><rect x="14" y="5" width="4" height="14"></rect></svg>`;
        } else {
            btn.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6,4 20,12 6,20"></polygon></svg>`;
        }
    }

    updateWaveformProgress(messageId, type, progress) {
        if (type === 'media') {
            this._updateMusicUI(messageId);
            return;
        }
        const slider = document.querySelector(`[data-voice-seek="${messageId}-${type}"]`);
        if (slider) {
            if (slider._userSeeking) {
                return;
            }
            slider.value = Math.round((progress / 100) * 1000);
            this.updateSeekSliderGradient(slider);
            return;
        }
        const waveform = document.querySelector(`[data-voice-waveform="${messageId}-${type}"]`);
        if (!waveform) {
            return;
        }
        const bars = waveform.querySelectorAll('.chat-voice-waveform-bar');
        const playedCount = Math.floor((progress / 100) * bars.length);
        bars.forEach((bar, i) => {
            bar.classList.toggle('chat-voice-waveform-bar--played', i < playedCount);
        });
    }

    seekAudio(messageId, type, value) {
        if (!VCS.state.chatAudioPlayers[messageId]?.[type]) {
            return;
        }
        const audio = VCS.state.chatAudioPlayers[messageId][type];
        if (audio.duration) {
            audio.currentTime = (value / 1000) * audio.duration;
        }
    }

    seekVoicephoto(e, messageId) {
        const bar = e.currentTarget;
        const audio = bar.closest('.chat-voicephoto').querySelector('.chat-voicephoto-audio');
        const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
        if (!dur) return;
        const rect = bar.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audio.currentTime = pct * dur;
    }

    onVoicephotoWords(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId] || [];
        const m = msgs.find(x => x.id === msg.messageId);
        if (m) {
            m._vpWords = msg.original || msg.words || [];
            if (msg.translated) { m._vpWordsTranslated = msg.translated; m._vpTargetLang = msg.targetLang; }
            if (msg.caption && !m.originalText) {
                m.originalText = msg.caption;
            }
            if (VCS.state.chatActiveChatId === msg.chatId) {
                VCS.chatMessages?.renderChatMessages?.();
                VCS.chatMessages?.scrollToBottom?.();
            }
        }
    }

    voicephotoKaraoke(audio, messageId, container) {
        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        const useTranslated = msg?._vpWordsTranslated && msg._vpTargetLang
            && msg._vpTargetLang === (VCS.state.currentLang || 'de') && msg.senderPhone !== VCS.state.chatMyPhone;
        const words = useTranslated ? msg._vpWordsTranslated : msg?._vpWords;
        const captionEl = container.querySelector('.chat-voicephoto-caption');
        if (!captionEl || !words || !words.length) return null;

        const segments = [];
        let cur = null;
        for (let i = 0; i < words.length; i++) {
            const w = words[i];
            const token = w.word || '';
            if (!token) continue;
            if (!cur) { cur = {start: w.start, end: w.end, words: [w]}; continue; }
            cur.end = w.end;
            cur.words.push(w);
            // Segment abschliessen bei Satzzeichen oder Sprechpause (> 0.5s)
            const next = words[i + 1];
            const gap = next ? next.start - w.end : 0;
            if (/[.!?]$/.test(token) || gap > 0.3) {
                segments.push(cur);
                cur = null;
            }
        }
        if (cur) segments.push(cur);

        let lastSegIdx = -1;
        let lastWordIdx = -1;

        const update = () => {
            const ct = audio.currentTime;
            let segIdx = -1;
            for (let i = 0; i < segments.length; i++) {
                if (ct >= segments[i].start - 0.1 && ct <= segments[i].end + 0.3) { segIdx = i; break; }
            }
            if (segIdx === -1) {
                for (let i = segments.length - 1; i >= 0; i--) {
                    if (ct >= segments[i].end && ct <= segments[i].end + 1.0) { segIdx = i; break; }
                }
            }

            if (segIdx === -1) {
                if (lastSegIdx !== -1) { captionEl.textContent = ''; lastSegIdx = -1; lastWordIdx = -1; }
                return;
            }

            const seg = segments[segIdx];
            if (segIdx !== lastSegIdx) {
                captionEl.innerHTML = '';
                seg.words.forEach((w, i) => {
                    const span = document.createElement('span');
                    span.textContent = (i > 0 ? ' ' : '') + w.word;
                    span.className = 'chat-vp-word';
                    captionEl.appendChild(span);
                });
                lastSegIdx = segIdx;
                lastWordIdx = -1;
            }

            const spans = captionEl.querySelectorAll('.chat-vp-word');
            let activeIdx = -1;
            for (let i = seg.words.length - 1; i >= 0; i--) {
                if (ct >= seg.words[i].start) { activeIdx = i; break; }
            }
            if (activeIdx !== lastWordIdx) {
                spans.forEach((s, i) => {
                    s.classList.toggle('chat-vp-word--active', i <= activeIdx);
                });
                lastWordIdx = activeIdx;
            }
        };

        return update;
    }

    playVoicephoto(btn, messageId) {
        const container = btn.closest('.chat-voicephoto');
        const audio = container.querySelector('.chat-voicephoto-audio');
        const playIcon = container.querySelector('.chat-voicephoto-icon-play');
        const pauseIcon = container.querySelector('.chat-voicephoto-icon-pause');
        const progressBar = container.querySelector('.chat-voicephoto-progress-bar');
        const durEl = container.querySelector('.chat-voicephoto-dur');

        document.querySelectorAll('.chat-voicephoto-audio').forEach(a => {
            if (a !== audio && !a.paused) {
                a.pause();
                a.currentTime = 0;
                const c = a.closest('.chat-voicephoto');
                c.querySelector('.chat-voicephoto-icon-play').classList.remove('hidden');
                c.querySelector('.chat-voicephoto-icon-pause').classList.add('hidden');
                c.querySelector('.chat-voicephoto-progress-bar').style.width = '0%';
                const h = c.querySelector('.chat-voicephoto-hint');
                const cap = c.querySelector('.chat-voicephoto-caption');
                if (h) h.classList.remove('hidden');
                if (cap) cap.classList.add('hidden');
            }
        });

        if (VCS.state.chatCurrentlyPlaying) {
            const prev = VCS.state.chatAudioPlayers[VCS.state.chatCurrentlyPlaying.messageId];
            if (prev && prev[VCS.state.chatCurrentlyPlaying.type]) {
                prev[VCS.state.chatCurrentlyPlaying.type].pause();
                this.updateVoiceUI(VCS.state.chatCurrentlyPlaying.messageId, VCS.state.chatCurrentlyPlaying.type, false);
            }
            VCS.state.chatCurrentlyPlaying = null;
        }

        if (!audio.paused) {
            audio.pause();
            playIcon.classList.remove('hidden');
            pauseIcon.classList.add('hidden');
            return;
        }

        if (audio.ended) audio.currentTime = 0;

        const hintEl = container.querySelector('.chat-voicephoto-hint');
        const captionEl = container.querySelector('.chat-voicephoto-caption');
        const karaokeUpdate = this.voicephotoKaraoke(audio, messageId, container);

        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msgData = msgs.find(m => m.id === messageId);
        const storedDurMs = msgData?.audioDurationMs || 0;
        const getDuration = () => isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (storedDurMs / 1000 || 0);

        if (hintEl) hintEl.classList.add('hidden');
        if (captionEl) {
            if (karaokeUpdate) captionEl.innerHTML = '';
            captionEl.classList.remove('hidden');
        }

        audio.ontimeupdate = () => {
            const dur = getDuration();
            if (!dur) return;
            const pct = (audio.currentTime / dur) * 100;
            progressBar.style.width = Math.min(pct, 100) + '%';
            const remaining = (dur - audio.currentTime) * 1000;
            durEl.textContent = VCS.chat?.formatDuration?.(remaining > 0 ? remaining : 0);
            if (karaokeUpdate) karaokeUpdate();
        };

        audio.onended = () => {
            playIcon.classList.remove('hidden');
            pauseIcon.classList.add('hidden');
            setTimeout(() => {
                if (audio.paused && audio.currentTime >= audio.duration - 0.1) {
                    audio.currentTime = 0;
                    progressBar.style.width = '0%';
                    const ms = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
                    const m = ms.find(x => x.id === messageId);
                    if (m) durEl.textContent = VCS.chat?.formatDuration?.(m.audioDurationMs || 0);
                    if (hintEl) hintEl.classList.remove('hidden');
                    if (captionEl) captionEl.classList.add('hidden');
                }
            }, 3000);
        };

        playIcon.classList.add('hidden');
        pauseIcon.classList.remove('hidden');
        audio.play().catch(() => {});
    }

    renderWaveformBars(seed) {
        const count = 24;
        let hash = 0;
        for (let i = 0; i < seed.length; i++) {
            hash = ((hash << 5) - hash) + seed.charCodeAt(i);
            hash |= 0;
        }
        let bars = '';
        for (let i = 0; i < count; i++) {
            hash = (hash * 1103515245 + 12345) & 0x7fffffff;
            const h = 4 + (hash % 22);
            bars += `<div class="chat-voice-waveform-bar" style="height:${h}px"></div>`;
        }
        return bars;
    }

    updateSeekSliderGradient(slider) {
        const pct = (slider.value / slider.max) * 100;
        const isTranslated = slider.classList.contains('chat-voice-seek--translated');
        const activeColor = isTranslated ? '#60a5fa' : '#f97316';
        const trackColor = isTranslated ? 'rgba(96,165,250,0.25)' : 'rgba(249,115,22,0.25)';
        slider.style.background = `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${pct}%, ${trackColor} ${pct}%, ${trackColor} 100%)`;
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // leer — Events werden in chatMessages.init() gebunden
    }

    // ── Private Methoden ─────────────────────────────────────

    _getAudioDuration(blob) {
        return new Promise((resolve) => {
            let resolved = false;
            const done = (dur) => { if (resolved) return; resolved = true; cleanup(); resolve(dur); };
            const audio = new Audio();
            const url = URL.createObjectURL(blob);
            audio.src = url;
            const cleanup = () => { try { URL.revokeObjectURL(url); } catch(_){} };

            setTimeout(() => done(5), 1500);

            audio.addEventListener('loadedmetadata', () => {
                if (isFinite(audio.duration) && audio.duration > 0) {
                    done(audio.duration);
                    return;
                }
                audio.currentTime = 1e10;
                audio.addEventListener('seeked', () => {
                    const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 5;
                    done(dur);
                }, {once: true});
            });
            audio.addEventListener('error', () => done(5));
        });
    }
};
